import { Injectable } from '@angular/core';
import { ApiService } from '../http/api.service';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpEventType } from '@angular/common/http';

export interface FileItem {
    id: string;
    remarks?: string | null;
    is_deleted: boolean;
    created_by: string;
    created_on: string;
    last_modified_by: string | null;
    last_modified_on: string;

    original_name: string;
    file_name: string;
    mime_type: string;
    size: number;
    url: string;
    thumbnail_url: string | null;
    metadata: Record<string, any>;
    storage_metadata: {
        key: string;
        bucket: string;
        region: string;
    };
    description: string | null;
    alt_text: string | null;
    is_public: boolean;
    tags: string[];
    playback_speed: number;
}

// First, create an interface to extend File
interface FileWithMetadata extends File {
    metadata?: Record<string, any>;
}

// Then modify the UploadProgress interface
export interface UploadProgress {
    id: string;
    file: FileWithMetadata;  // Change File to FileWithMetadata
    progress: number;
    status: 'queued' | 'pending' | 'uploading' | 'completed' | 'error';
    error?: string;
    position?: number;
}

export interface BatchOperation {
    items: { id: string; type: 'FILE' | 'FOLDER' }[];
    targetFolderId?: string;
}

export interface ApiResponse<T> {
    version: string;
    data: {
        items: T[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

@Injectable({
    providedIn: 'root'
})
export class FileManagerService {
    private readonly ADMIN_BASE_PATH = 'admin/files';
    private readonly USER_BASE_PATH = 'content-files';
    private readonly MAX_CONCURRENT_UPLOADS = 1; // Maximum concurrent uploads
    private BASE_PATH = this.ADMIN_BASE_PATH;
    private uploadQueue: UploadProgress[] = [];
    private activeUploads = new Map<string, UploadProgress>();
    private uploadProgress$ = new Subject<UploadProgress>();
    private queueStatus$ = new BehaviorSubject<{
        total: number;
        active: number;
        queued: number;
        completed: number;
        failed: number;
    }>({ total: 0, active: 0, queued: 0, completed: 0, failed: 0 });

    constructor(private api: ApiService) { }

    setBasePath(userMode: boolean) {
        this.BASE_PATH = userMode ? this.USER_BASE_PATH : this.ADMIN_BASE_PATH;
    }

    getFile(id: string): Observable<FileItem> {
        return this.api.get<{ success: boolean, data: FileItem }>(`${this.BASE_PATH}/${id}`).pipe(
            map(response => response.data)
        );
    }

    deleteFile(id: string): Observable<any> {
        return this.api.delete(`${this.BASE_PATH}/${id}`);
    }

    listFiles(params: any = {}): Observable<ApiResponse<FileItem>> {
        return this.api.get<ApiResponse<FileItem>>(this.BASE_PATH, {
            ...params,
        });
    }

    updateFilePlaybackSpeed(id: string, playbackSpeed: number): Observable<FileItem> {
        return this.api.put<{ success: boolean, data: FileItem }>(`${this.BASE_PATH}/${id}/playback-speed`, {
            playback_speed: playbackSpeed
        }).pipe(
            map(response => response.data)
        );
    }

    uploadFile(file: File, folderId?: string, metadata?: Record<string, any>): string {
        const uploadId = Math.random().toString(36).substring(7);
        
        const progress: UploadProgress = {
            id: uploadId,
            file,
            progress: 0,
            status: 'queued',
            position: this.uploadQueue.length + 1
        };

        this.uploadQueue.push(progress);
        this.activeUploads.set(uploadId, progress);
        this.uploadProgress$.next(progress);
        this.updateQueueStatus();

        // Try to process queue
        this.processQueue();

        return uploadId;
    }

    private processQueue() {
        // Count current active uploads
        const activeUploads = Array.from(this.activeUploads.values())
            .filter(upload => upload.status === 'uploading').length;

        // Calculate how many more uploads we can start
        const availableSlots = this.MAX_CONCURRENT_UPLOADS - activeUploads;

        if (availableSlots > 0) {
            // Get next items to upload
            const nextUploads = this.uploadQueue
                .filter(upload => upload.status === 'queued')
                .slice(0, availableSlots);

            // Start uploading each item
            nextUploads.forEach(upload => this.startUpload(upload));
        }
    }

    private startUpload(upload: UploadProgress) {
        const formData = new FormData();
        formData.append('file', upload.file);

        if (upload.file.metadata) {
            formData.append('metadata', JSON.stringify(upload.file.metadata));
        }

        upload.status = 'uploading';
        this.uploadProgress$.next({ ...upload });
        this.updateQueueStatus();

        const url = `${this.BASE_PATH}`;

        console.log(url, formData, upload);
        this.api.uploadFileWithProgress(url, formData, upload, (progress: number) => {
            upload.progress = progress;
            this.uploadProgress$.next({ ...upload });
        }).subscribe({
            next: (event) => {
                console.log(event);
                upload.status = 'completed';
                upload.progress = 100;
                this.uploadProgress$.next({ ...upload });
                this.updateQueueStatus();

                // Remove from active uploads after a delay
                setTimeout(() => {
                    this.activeUploads.delete(upload.id);
                    this.updateQueueStatus();
                }, 3000);

                // Process next items in queue
                this.processQueue();
            }
        });
        // this.api.uploadFormData(url, formData, {
        //     headers: {
        //         'accept': 'text/event-stream'
        //     },
        //     reportProgress: true,
        //     observe: 'events',
        //     onUploadProgress: (progressEvent: any) => {
        //         console.log(progressEvent);
        //         const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        //         upload.progress = percentCompleted;
        //         this.uploadProgress$.next({ ...upload });
        //     }
        // }).subscribe({
        //     next: (event) => {
        //         console.log(event);
        //         switch (event.type) {
        //           case HttpEventType.UploadProgress:
        //             if (event.total) {
        //               const percentCompleted = Math.round((event.loaded * 100) / event.total);
        //               upload.progress = percentCompleted;
        //               this.uploadProgress$.next({ ...upload });
        //             }
        //             break;
        //           case HttpEventType.Response:
        //             console.log(event.body);
        //             upload.status = 'completed';
        //             upload.progress = 100;
        //             this.uploadProgress$.next({ ...upload });
        //             this.updateQueueStatus();
    
        //             // Remove from active uploads after a delay
        //             setTimeout(() => {
        //                 this.activeUploads.delete(upload.id);
        //                 this.updateQueueStatus();
        //             }, 3000);
    
        //             // Process next items in queue
        //             this.processQueue();
        //             // observer.complete();
        //             break;
        //         }
        //     },
        //     error: (error) => {
        //         upload.status = 'error';
        //         upload.error = error.message;
        //         this.uploadProgress$.next({ ...upload });
        //         this.updateQueueStatus();

        //         // Try to process next items even if this one failed
        //         this.processQueue();
        //     }
        // });
    }

    private updateQueueStatus() {
        const uploads = Array.from(this.activeUploads.values());
        const status = {
            total: uploads.length,
            active: uploads.filter(u => u.status === 'uploading').length,
            queued: uploads.filter(u => u.status === 'queued').length,
            completed: uploads.filter(u => u.status === 'completed').length,
            failed: uploads.filter(u => u.status === 'error').length
        };
        this.queueStatus$.next(status);
    }

    getUploadProgress(): Observable<UploadProgress> {
        return this.uploadProgress$.asObservable();
    }

    getQueueStatus(): Observable<{
        total: number;
        active: number;
        queued: number;
        completed: number;
        failed: number;
    }> {
        return this.queueStatus$.asObservable();
    }

    getActiveUploads(): UploadProgress[] {
        return Array.from(this.activeUploads.values());
    }

    cancelUpload(uploadId: string): void {
        const upload = this.activeUploads.get(uploadId);
        if (upload) {
            if (upload.status === 'queued') {
                // Remove from queue if not started
                this.uploadQueue = this.uploadQueue.filter(u => u.id !== uploadId);
            }
            upload.status = 'error';
            upload.error = 'Upload cancelled';
            this.uploadProgress$.next({ ...upload });
            this.activeUploads.delete(uploadId);
            this.updateQueueStatus();
            
            // Process queue in case there are pending uploads
            this.processQueue();
        }
    }

} 